import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  MenuItem,
  Select,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate } from "react-router-dom";

const TIPO_LABEL = {
  descuento: "Descuento (%)",
  precio_fijo: "Descuento Fijo ($)",
  "2x1": "2x1",
  destacado: "Producto Destacado",
  mas_vendido: "Más Vendido",
};

const TIPO_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "descuento", label: "Descuento (%)" },
  { value: "precio_fijo", label: "Descuento Fijo ($)" },
  { value: "2x1", label: "2x1" },
  { value: "destacado", label: "Producto Destacado" },
  { value: "mas_vendido", label: "Más Vendido" },
];

const AdminPromotion = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedPromoId, setSelectedPromoId] = useState(null);

  const fetchPromotions = async () => {
    try {
      const resp = await fetch("/ofertas/listar/", { method: "GET", credentials: "include" });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Error al cargar promociones.");
      const items = Array.isArray(data.promociones) ? data.promociones : [];
      setPromotions(items);
    } catch (err) {
      setErrorMessage(err.message || "Error al cargar promociones.");
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  useEffect(() => {
    if (localStorage.getItem("promo_creada") === "true") {
      setSuccessMessage("Promoción creada con éxito.");
      localStorage.removeItem("promo_creada");
    }
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return promotions.filter((p) => {
      const target = p.producto || p.producto_base || {};
      const variante = p.variante || {};
      const texto = `${target.nombre || ""} ${target.codigo || ""} ${variante.sku || ""} ${variante.value || ""}`.toLowerCase();
      const okTexto = q ? texto.includes(q) : true;
      const okTipo = tipoFilter ? p.tipo === tipoFilter : true;
      return okTexto && okTipo;
    });
  }, [promotions, search, tipoFilter]);

  const handleDeleteClick = (id) => {
    setSelectedPromoId(id);
    setConfirmOpen(true);
  };

  const getCSRFToken = () => {
    const match = document.cookie.match(/(^| )csrftoken=([^;]+)/);
    return match ? match[2] : "";
  };

  const handleConfirmDelete = async () => {
    try {
      const resp = await fetch(`/ofertas/eliminar/${selectedPromoId}/`, {
        method: "DELETE",
        credentials: "include",
        headers: { "X-CSRFToken": getCSRFToken() },
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.error || "No se pudo eliminar la promoción.");
      setSuccessMessage(data.message || "Promoción eliminada.");
      fetchPromotions();
    } catch (err) {
      setErrorMessage(err.message || "Error al eliminar promoción.");
    } finally {
      setConfirmOpen(false);
      setSelectedPromoId(null);
    }
  };

  const formatValor = (tipo, valor) => {
    if (valor === null || valor === undefined) return "—";
    if (tipo === "descuento") return `${Number(valor).toFixed(2)} %`;
    if (tipo === "precio_fijo") return `$ ${Number(valor).toFixed(2)}`;
    return "—";
  };

  const formatVigencia = (p) => {
    if (p.sin_fecha_fin) return "Indefinida";
    const i = p.inicio || "";
    const f = p.fin || "";
    return `${i || "—"}${f ? " - " + f : ""}`;
  };

  const getIvaPct = (promo) => {
    if (promo.variante && typeof promo.variante.iva_porcentaje !== "undefined") return Number(promo.variante.iva_porcentaje || 0);
    if (promo.producto && typeof promo.producto.iva_porcentaje !== "undefined") return Number(promo.producto.iva_porcentaje || 0);
    if (promo.producto_base && typeof promo.producto_base.iva_porcentaje !== "undefined") return Number(promo.producto_base.iva_porcentaje || 0);
    return 0;
  };

  const getIvaValue = (promo) => {
    if (promo.variante) {
      const pct = typeof promo.variante.iva_porcentaje !== "undefined" ? Number(promo.variante.iva_porcentaje || 0) : getIvaPct(promo);
      const base = promo.variante.precio_descuento != null ? Number(promo.variante.precio_descuento || 0) : Number(promo.variante.price || 0);
      return (base * pct) / 100;
    }
    const target = promo.producto || promo.producto_base || null;
    if (!target) return 0;
    const pct = typeof target.iva_porcentaje !== "undefined" ? Number(target.iva_porcentaje || 0) : getIvaPct(promo);
    const base = target.precio_descuento != null ? Number(target.precio_descuento || 0) : Number(target.pvp1 || 0);
    return (base * pct) / 100;
  };

  const handleSelect = (component) => {
    if (component === "AdminSection") navigate("/admin-panel/secciones");
    else if (component === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (component === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (component === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (component === "AdminProforma") navigate("/admin-panel/proformas");
    else if (component === "AdminServices") navigate("/admin-panel/servicios");
    else if (component === "AdminClient") navigate("/admin-panel/clientes");
    else if (component === "AdminPromotion") navigate("/admin-panel/promociones");
    else navigate("/admin-panel");
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.removeItem("user_email");
    localStorage.removeItem("is_superuser");
    localStorage.removeItem("is_admin");
    localStorage.removeItem("is_logged_in");
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        handleSelect={handleSelect}
        handleLogout={handleLogout}
        navigate={navigate}
      />
      <Box sx={{ flexGrow: 1, transition: "all 0.3s ease" }}>
        <TopBar open={open} setOpen={setOpen} setSelectedComponent={() => navigate("/admin-panel")} />
        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ textTransform: "none", bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
              onClick={() => navigate("/admin-panel/promociones/nueva")}
            >
              Crear Promoción
            </Button>
          </Box>

          <Paper sx={{ p: 3, borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", bgcolor: "#FFFFFF" }}>
            <Typography variant="h6" mb={2}>
              Promociones
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                label="Buscar promoción"
                variant="outlined"
                fullWidth
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Select
                size="small"
                value={tipoFilter}
                onChange={(e) => {
                  setTipoFilter(e.target.value);
                  setPage(0);
                }}
                displayEmpty
                sx={{ minWidth: 200, bgcolor: "#fff" }}
              >
                {TIPO_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#1565C0" }}>
                  <TableCell sx={{ color: "#fff" }}>Producto / Variante</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Tipo</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Valor</TableCell>
                  <TableCell sx={{ color: "#fff" }}>IVA (%)</TableCell>
                  <TableCell sx={{ color: "#fff" }}>IVA $</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Estado</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Vigencia</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((promo) => {
                  const targetProd = promo.producto || promo.producto_base || null;
                  const targetVar = promo.variante || null;
                  const nombre = targetVar
                    ? `${targetProd?.nombre || ""} — ${targetVar.variant_type}: ${targetVar.value}`
                    : targetProd?.nombre || "";
                  const extra = targetVar
                    ? `SKU ${targetVar.sku} · $${Number(targetVar.price || 0).toFixed(2)}`
                    : `${targetProd?.codigo || ""} · $${Number(targetProd?.pvp1 || 0).toFixed(2)}`;
                  const ivaPct = getIvaPct(promo);
                  const ivaVal = getIvaValue(promo);
                  return (
                    <TableRow key={promo.id}>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography fontWeight={600}>{nombre}</Typography>
                          <Typography variant="body2" color="text.secondary">{extra}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={TIPO_LABEL[promo.tipo] || promo.tipo} size="small" />
                      </TableCell>
                      <TableCell>{formatValor(promo.tipo, promo.valor)}</TableCell>
                      <TableCell>{`${ivaPct.toFixed(0)} %`}</TableCell>
                      <TableCell>{`$ ${ivaVal.toFixed(2)}`}</TableCell>
                      <TableCell>{promo.activo ? "Activa" : "Inactiva"}</TableCell>
                      <TableCell>{formatVigencia(promo)}</TableCell>
                      <TableCell>
                        <IconButton color="error" onClick={() => handleDeleteClick(promo.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography sx={{ p: 2 }}>No hay promociones registradas.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10]}
              sx={{
                "& .MuiTablePagination-caption": { fontSize: "0.8rem" },
                "& .MuiTablePagination-selectLabel": { fontSize: "0.8rem" },
                "& .MuiTablePagination-select": { fontSize: "0.8rem" },
              }}
            />
          </Paper>

          <Snackbar
            open={!!successMessage}
            autoHideDuration={3000}
            onClose={() => setSuccessMessage("")}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert severity="success">{successMessage}</Alert>
          </Snackbar>

          <Snackbar
            open={!!errorMessage}
            autoHideDuration={3000}
            onClose={() => setErrorMessage("")}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert severity="error">{errorMessage}</Alert>
          </Snackbar>

          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>Eliminar Promoción?</DialogTitle>
            <DialogContent>
              <Typography>¿Estás seguro de que deseas eliminar esta promoción?</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
              <Button color="error" onClick={handleConfirmDelete}>
                Eliminar
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPromotion;
